<template>
  <div class="pg-adc">
    <!-- 섹션 레벨: 제목 -->
    <div class="p-field">
      <label class="p-field__label">Section Title</label>
      <input
        v-model="sectionData.content.sectionTitle"
        type="text"
        class="p-input"
        placeholder="섹션 제목"
      />
    </div>

    <!-- 문항 탭 -->
    <question-tabs
      :count="questions.length"
      v-model="currentIndex"
      @add="addQuestion"
      @remove="removeQuestion"
    />

    <template v-if="current">
      <div class="p-form-grid p-form-grid--2">
        <div class="p-field">
          <label class="p-field__label">Question / 지시문</label>
          <input
            v-model="current.questionText"
            type="text"
            class="p-input"
            placeholder="예: 음성을 듣고 말한 개수만큼 그림을 바구니에 담으세요"
          />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round pg-adc__audio-ico">music_note</i>
            Audio URL (단어 + 수 발음)
          </label>
          <input
            v-model="current.audioUrl"
            type="text"
            class="p-input"
            placeholder="음성 URL (풀 URL 권장)"
          />
        </div>
      </div>

      <div class="p-form-grid p-form-grid--2">
        <div class="p-field">
          <label class="p-field__label">음성 스크립트 (선택 · 예: "우유 셋")</label>
          <input
            v-model="current.audioScript"
            type="text"
            class="p-input"
            placeholder="음성 내용 표기 (미리보기·참고용)"
          />
        </div>
        <div class="p-field">
          <label class="p-field__label">바구니 삽화 (선택)</label>
          <image-upload
            :default-image="current.basketImage || require('@/assets/img/no_image.png')"
            :unique-id="'adc-basket-' + currentIndex"
            v-model="current.basketImage"
          />
        </div>
      </div>

      <!-- 정답 요약 미리보기 -->
      <div class="pg-adc__preview">
        <span class="pg-adc__preview-label">정답</span>
        <span class="pg-adc__preview-text">{{ answerSummary }}</span>
      </div>

      <!-- 그림 카드 -->
      <div class="pg-adc__cards">
        <div
          v-for="(card, index) in current.cards"
          :key="index"
          class="pg-adc__item"
          :class="{ 'is-answer': Number(card.count) > 0 }"
        >
          <div class="pg-adc__item-head">
            <span class="pg-adc__index">Card {{ index + 1 }}</span>
            <span v-if="Number(card.count) > 0" class="pg-adc__badge">
              <i class="material-icons-round">check_circle</i>정답 · {{ card.count }}개
            </span>
            <button
              type="button"
              class="pg-adc__icon-btn pg-adc__icon-btn--danger"
              title="제거"
              @click.prevent="removeCard(index)"
            >
              <i class="material-icons-round">delete</i>
            </button>
          </div>
          <div class="pg-adc__item-body">
            <div class="p-field">
              <label class="p-field__label">Label (그림 이름)</label>
              <input
                v-model="card.label"
                type="text"
                class="p-input"
                placeholder="예: 우유"
              />
            </div>
            <div class="p-field">
              <label class="p-field__label">담을 개수 (0 = 오답 카드)</label>
              <input
                v-model.number="card.count"
                type="number"
                min="0"
                class="p-input"
                placeholder="0"
              />
            </div>
            <div class="pg-adc__image">
              <label class="p-field__label">그림 카드 이미지</label>
              <image-upload
                :default-image="card.image || require('@/assets/img/no_image.png')"
                :unique-id="'adc-card-' + currentIndex + '-' + index"
                v-model="card.image"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="pg-adc__actions">
        <ABtn kind="yellow" icon="add" @click.prevent="addCard">카드 추가</ABtn>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ImageUpload from "@/components/ImageUpload.vue";
import QuestionTabs from "@/components/Practice/QuestionTabs.vue";
import { useQuestions } from "@/components/Practice/useQuestions";

const sectionData = defineModel({ type: Object, required: true });

const makeEmpty = () => ({
  questionText: "",
  audioUrl: "",
  audioScript: "",
  basketImage: "",
  cards: [{ label: "", image: "", count: 0 }]
});

const { questions, currentIndex, current, addQuestion, removeQuestion } = useQuestions(
  sectionData,
  makeEmpty,
  ["questionText", "audioUrl", "audioScript", "basketImage", "cards"]
);

const answerSummary = computed(() => {
  if (!current.value) return "";
  const answers = (current.value.cards || [])
    .filter((c) => Number(c.count) > 0)
    .map((c) => `${c.label || "□"} ${c.count}개`);
  return answers.length ? answers.join(", ") : "정답 카드를 지정하세요 (개수 > 0)";
});

const addCard = () => {
  current.value.cards.push({ label: "", image: "", count: 0 });
};

const removeCard = (index) => {
  current.value.cards.splice(index, 1);
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-adc {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__audio-ico {
    font-size: 14px !important;
    color: $p-point-deep;
    vertical-align: middle;
    margin-right: 4px;
  }

  // ── 정답 미리보기 ──
  &__preview {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border: 1px dashed $p-line-hard;
    border-radius: $p-radius-md;
    background: $p-bg;
  }

  &__preview-label {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: $p-ink-soft;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  &__preview-text {
    font-size: 15px;
    font-weight: 600;
    color: $p-ink;
  }

  // ── 카드 ──
  &__cards {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    transition: border-color $p-dur $p-ease, background $p-dur $p-ease;

    &:hover { border-color: $p-line-hard; }

    &.is-answer {
      border-color: $p-ok;
      background: $p-ok-soft;
    }
  }

  &__item-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__index {
    padding: 4px 12px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 700;
    color: $p-ok;

    > i { font-size: 16px; }
  }

  &__icon-btn {
    margin-left: auto;
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &--danger {
      color: $p-err;
      &:hover { background: $p-err-soft; }
    }
  }

  &__item-body {
    display: grid;
    grid-template-columns: 1fr 140px 200px;
    gap: 16px;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__image {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}
</style>
